import { assertNever } from "@impero-com/common/utils/obj";

import {
    InitialAssignmentKind,
    SubsequentAssignmentKind,
} from "api/generated/model";

function validateSod(
    assigneeKind: InitialAssignmentKind,
    finalReviewerKind: SubsequentAssignmentKind,
    firstReviewerKind: SubsequentAssignmentKind | undefined
): SodValidationResult {
    let assigneeUserId: number | undefined;
    let defaultClaimantUserId: number | undefined;
    switch (assigneeKind.type) {
        case "User":
            assigneeUserId = assigneeKind.userId;
            defaultClaimantUserId = undefined;
            break;
        case "Pool":
            assigneeUserId = undefined;
            defaultClaimantUserId =
                assigneeKind.defaultClaimantId !== null
                    ? assigneeKind.defaultClaimantId
                    : undefined;
            break;
        case "Group":
            assigneeUserId = undefined;
            defaultClaimantUserId = undefined;
            break;
        default:
            return assertNever(assigneeKind);
    }
    let firstReviewerUserId: number | undefined;
    let secondReviewerUserId: number | undefined;
    if (!firstReviewerKind) {
        firstReviewerUserId = getReviewerUserId(finalReviewerKind);
        secondReviewerUserId = undefined;
    } else {
        firstReviewerUserId = getReviewerUserId(firstReviewerKind);
        secondReviewerUserId = getReviewerUserId(finalReviewerKind);
    }
    return computeSodErrorsAndWarnings(
        assigneeUserId,
        defaultClaimantUserId,
        firstReviewerUserId,
        secondReviewerUserId
    );
}

function computeSodErrorsAndWarnings(
    assigneeUserId: number | undefined,
    defaultClaimantUserId: number | undefined,
    firstReviewerUserId: number | undefined,
    secondReviewerUserId: number | undefined
): SodValidationResult {
    let defaultClaimantError: DefaultClaimantError | undefined;
    if (
        defaultClaimantUserId !== undefined &&
        (defaultClaimantUserId === firstReviewerUserId ||
            defaultClaimantUserId === secondReviewerUserId)
    ) {
        defaultClaimantError = "DefaultClaimantShouldBeDifferentFromReviewers";
    } else {
        defaultClaimantError = undefined;
    }
    let firstReviewerError: FirstReviewerErrors | undefined;
    let secondReviewerError: SecondReviewerErrors | undefined;
    if (assigneeUserId !== undefined) {
        if (assigneeUserId === firstReviewerUserId) {
            firstReviewerError = "ReviewerShouldBeDifferentFromResponsible";
        }
        if (assigneeUserId === secondReviewerUserId) {
            secondReviewerError =
                "SecondReviewerShouldBeDifferentFromResponsible";
        }
    }
    if (
        firstReviewerUserId !== undefined &&
        firstReviewerUserId === secondReviewerUserId &&
        !secondReviewerError
    ) {
        secondReviewerError = "SecondReviewerShouldBeDifferentFromReviewer";
    }

    if (defaultClaimantError || firstReviewerError || secondReviewerError) {
        return {
            type: "SodIssues",
            defaultClaimantError,
            firstReviewerError,
            secondReviewerError,
        };
    } else {
        return { type: "NoSodIssues" };
    }
}

function getReviewerUserId(
    reviewerKind: SubsequentAssignmentKind
): number | undefined {
    if (reviewerKind.type === "User") {
        return reviewerKind.userId;
    } else {
        return undefined;
    }
}

type SodValidationResult = SodValidationSuccess | SodValidationIssues;

interface SodValidationSuccess {
    type: "NoSodIssues";
}

interface SodValidationIssues {
    type: "SodIssues";
    firstReviewerError: FirstReviewerErrors | undefined;
    secondReviewerError: SecondReviewerErrors | undefined;
    defaultClaimantError: DefaultClaimantError | undefined;
}

type FirstReviewerErrors = "ReviewerShouldBeDifferentFromResponsible";

type SecondReviewerErrors =
    | "SecondReviewerShouldBeDifferentFromResponsible"
    | "SecondReviewerShouldBeDifferentFromReviewer";

type DefaultClaimantError = "DefaultClaimantShouldBeDifferentFromReviewers";

export { validateSod };
export type { SodValidationResult, SodValidationIssues };
